import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";
import { Get } from "../api/apiUtils";
import { ApiCallModel, LineHandling, Nomination } from "../models";
import { PersistentDataService } from "../services/PersistentDataService";
import { SetActiveConfigAction } from "./ProcessFlowConfigStore";

export interface LineHandlingState {
  lineHandling: ApiCallModel<LineHandling>;
  nomination: Nomination | undefined;
  selectedState: string | undefined;
}

export const defaultState = {
  lineHandling: { isError: false, isLoading: false, result: undefined },
  nomination: undefined,
  selectedState: undefined
} as LineHandlingState;

export interface SetLineHandlingAction {
  type: "SET_LINE_HANDLING";
  payload: ApiCallModel<LineHandling>;
}

export interface SetNominationAction {
  type: "SET_NOMINATION";
  nomination: Nomination;
}

export interface SetSelectedStateAction {
  type: "SET_SELECTED_STATE";
  state: string;
}

type KnownAction =
  | SetLineHandlingAction
  | SetNominationAction
  | SetSelectedStateAction;

export const actionCreators = {
  getLineHandling:
    (
      lineHandlingNo: string,
      nominationNo: string | undefined,
      cacheFirst: boolean,
      retainState: boolean = false,
      success?: (() => void) | undefined
    ): AppThunkAction<
      | SetLineHandlingAction
      | SetSelectedStateAction
      | SetNominationAction
      | SetActiveConfigAction
    > =>
    (dispatch, getState) => {
      const state = getState();
      dispatch({
        type: "SET_LINE_HANDLING",
        payload: {
          isLoading: true,
          isError: false,
          result: undefined
        }
      });

      lineHandlingNo = lineHandlingNo.toLowerCase();
      Get(`api/linehandling/${lineHandlingNo}`, cacheFirst)
        .then((response) => response.json())
        .then((result: LineHandling) => {
          dispatch({
            type: "SET_LINE_HANDLING",
            payload: {
              isLoading: false,
              isError: false,
              result
            }
          });

          if (success) success();
          if (nominationNo) {
            const nomination = result.nominations.find(
              (p) => p.nominationNo.toLowerCase() === nominationNo.toLowerCase()
            );
            if (nomination) {
              dispatch({ type: "SET_NOMINATION", nomination });

              if (!retainState) {
                dispatch({
                  type: "SET_SELECTED_STATE",
                  state: nomination.currentState
                });
              }

              var config =
                state.processFlowConfigState.processFlowConfigs.result &&
                state.processFlowConfigState.processFlowConfigs.result.find(
                  (f) =>
                    f.id === nomination.processFlowConfigId &&
                    f.version === nomination.processFlowConfigVersion
                );
              if (config) {
                dispatch({ type: "SET_ACTIVE_CONFIG", payload: config });
              }
            }
          } else {
            if (result.currentState && !retainState) {
              dispatch({
                type: "SET_SELECTED_STATE",
                state: result.currentState
              });
            }

            var config =
              state.processFlowConfigState.processFlowConfigs.result &&
              state.processFlowConfigState.processFlowConfigs.result.find(
                (f) =>
                  f.id === result.processFlowConfigId &&
                  f.version === result.processFlowConfigVersion
              );
            dispatch({ type: "SET_ACTIVE_CONFIG", payload: config });
          }
        })
        .catch((reason) =>
          dispatch({
            type: "SET_LINE_HANDLING",
            payload: { isError: true, isLoading: false, result: undefined }
          })
        );
    },
  setLineHandling: (lineHandling: LineHandling) => ({
    type: "SET_LINE_HANDLING",
    payload: { isLoading: false, isError: false, result: lineHandling }
  }),
  setActiveNomination:
    (
      nominatioNo: string,
      setSelectedState: boolean = true
    ): AppThunkAction<
      SetNominationAction | SetSelectedStateAction | SetActiveConfigAction
    > =>
    (dispatch, getState) => {
      const state = getState();

      if (state.lineHandling && state.lineHandling.lineHandling.result) {
        const nomination =
          state.lineHandling.lineHandling.result.nominations.find(
            (f) => f.nominationNo.toLowerCase() === nominatioNo.toLowerCase()
          );

        if (nomination) {
          dispatch({ type: "SET_NOMINATION", nomination });

          if (setSelectedState) {
            if (nomination.currentState) {
              dispatch({
                type: "SET_SELECTED_STATE",
                state: nomination.currentState
              });
            }
          }

          if (
            state.processFlowConfigState.config &&
            state.processFlowConfigState.processFlowConfigs.result &&
            nomination.processFlowConfigId
          ) {
            const config =
              state.processFlowConfigState.processFlowConfigs.result.find(
                (f) =>
                  f.id === nomination.processFlowConfigId &&
                  f.version === nomination.processFlowConfigVersion
              );
            if (config) {
              dispatch({ type: "SET_ACTIVE_CONFIG", payload: config });
            }
          }
        }
      }
    },
  deleteActiveLineHandling: () => ({
    type: "SET_LINE_HANDLING",
    payload: { isError: false, isLoading: false, result: undefined }
  }),
  deleteActiveNomination: () => ({
    type: "SET_NOMINATION",
    nomination: undefined
  }),
  setSelectedState: (state: string) => ({ type: "SET_SELECTED_STATE", state })
};

export const reducers: Reducer<LineHandlingState> = (
  state: LineHandlingState | undefined,
  incomingAction: Action
): LineHandlingState => {
  if (state === undefined) {
    return defaultState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case "SET_LINE_HANDLING":
      if (action.payload.result) {
        PersistentDataService.saveLineHandling(action.payload.result);
      }
      return { ...state, lineHandling: action.payload };
    case "SET_NOMINATION":
      return { ...state, nomination: action.nomination };
    case "SET_SELECTED_STATE":
      return { ...state, selectedState: action.state };
    default:
      return state;
  }
};
